import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useServices } from '@/app/services';
import type { Session } from '@/api/session';
import type { SyncState } from '@/data/sync';
import { compareNotes, type LocalNote } from '@/domain/note';
import { allTagPaths, buildFolderTree, type Folder } from '@/domain/tags';

/** The current session (null when signed out). */
export function useSession(): Session | null {
  const { sessions } = useServices();
  return useSyncExternalStore(
    useCallback((cb) => sessions.subscribe(cb), [sessions]),
    () => sessions.get(),
  );
}

/** Same stored state (derived `tags` follow from title and content). */
function sameNote(a: LocalNote, b: LocalNote): boolean {
  return (Object.keys(b) as (keyof LocalNote)[]).every((k) => k === 'tags' || a[k] === b[k]);
}

/**
 * Live list of all visible (not deleted) notes, sorted. Unchanged notes keep
 * their object identity across updates, so memoized cards do not re-render
 * when a sync or an edit touches other notes.
 */
export function useNotes(): LocalNote[] | undefined {
  const { db } = useServices();
  const previous = useRef(new Map<string, LocalNote>());
  return useLiveQuery(async () => {
    const all = await db.notes.toArray();
    const next = new Map<string, LocalNote>();
    const visible = all
      .filter((n) => !n.deleted)
      .map((n) => {
        const prev = previous.current.get(n.id);
        const note = prev && sameNote(prev, n) ? prev : n;
        next.set(n.id, note);
        return note;
      });
    previous.current = next;
    return visible.sort(compareNotes);
  }, [db]);
}

/** Live single note. `null` = not found, `undefined` = loading. */
export function useNote(id: string | undefined): LocalNote | null | undefined {
  const { db } = useServices();
  return useLiveQuery(async () => (id ? ((await db.notes.get(id)) ?? null) : null), [db, id]);
}

/** Folder tree and all known tag paths derived from the notes. */
export function useFolders(notes: LocalNote[] | undefined): { tree: Folder[]; tags: string[] } {
  return useMemo(() => {
    const active = (notes ?? []).filter((n) => !n.archived);
    const lists = active.map((n) => n.tags);
    return { tree: buildFolderTree(lists), tags: allTagPaths((notes ?? []).map((n) => n.tags)) };
  }, [notes]);
}

export function useSyncState(): SyncState {
  const { sync } = useServices();
  return useSyncExternalStore(
    useCallback((cb) => sync.subscribe(cb), [sync]),
    () => sync.getState(),
  );
}

export function useOnline(): boolean {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener('online', cb);
      window.addEventListener('offline', cb);
      return () => {
        window.removeEventListener('online', cb);
        window.removeEventListener('offline', cb);
      };
    },
    () => navigator.onLine,
  );
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/** Debounces a value. */
export function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}
